/**
 * Schedule templates — saved WHEN patterns the user can reuse.
 *
 * A template captures the time-of-day, recurrence, and date offset of a
 * schedule, but NOT the content or the screens. Use case: marketing wants
 * to schedule "weekday lunch (12-1pm Mon-Fri)" or "evening promo (6-10pm
 * every day)" repeatedly, without re-typing the same fields.
 *
 * Storage: localStorage for v1. Backend persistence (sumadhura_schedule_template
 * collection + CRUD endpoints) is a deferred Phase E.2 if usage justifies it.
 *
 * Template shape:
 * {
 *   id: "tpl_<timestamp>",
 *   name: "Weekday lunch",
 *   startTime: "12:00",       // HH:MM
 *   endTime:   "13:00",       // HH:MM
 *   recurrence: "weekdays",   // 'once' | 'daily' | 'weekdays' | 'weekends'
 *   durationDays: 7,          // for non-once: how many days the date range spans
 *                             //  (start = today, end = today + durationDays - 1)
 *   createdAt: 1712779200000,
 * }
 *
 * Templates DO NOT store contentId or screenIds. The user picks those fresh
 * each time the modal opens — templates are about the time pattern only.
 */

const STORAGE_KEY = 'sumadhura.schedule.templates.v1';
const MAX_TEMPLATES = 50; // hard cap so localStorage doesn't bloat

const safeParse = (json, fallback) => {
  try {
    const v = JSON.parse(json);
    return Array.isArray(v) ? v : fallback;
  } catch {
    return fallback;
  }
};

/** Read all templates from localStorage. Always returns an array. */
export const listTemplates = () => {
  if (typeof window === 'undefined' || !window.localStorage) return [];
  return safeParse(window.localStorage.getItem(STORAGE_KEY), []);
};

/** Sort templates by createdAt descending (newest first) for the picker. */
export const listTemplatesSorted = () => {
  return listTemplates().sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
};

/**
 * Save a new template. Returns the saved template (with id + createdAt).
 * If maxed out, the oldest is dropped to make room.
 */
export const saveTemplate = ({ name, startTime, endTime, recurrence, durationDays }) => {
  const trimmedName = String(name || '').trim();
  if (!trimmedName) throw new Error('Template name is required');
  if (!startTime || !endTime) throw new Error('Time range is required');
  const all = listTemplates();
  const tpl = {
    id: `tpl_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    name: trimmedName.slice(0, 60),
    startTime,
    endTime,
    recurrence: recurrence || 'once',
    durationDays: Number(durationDays) || 1,
    createdAt: Date.now(),
  };
  const next = [tpl, ...all].slice(0, MAX_TEMPLATES);
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch (err) {
    // localStorage full or disabled — non-fatal
    console.warn('Could not save schedule template:', err.message);
  }
  return tpl;
};

/** Delete a template by id. */
export const deleteTemplate = (id) => {
  const all = listTemplates();
  const next = all.filter((t) => t.id !== id);
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch (err) {
    console.warn('Could not delete schedule template:', err.message);
  }
};

/**
 * Compute the start/end dates that result from applying a template to "today".
 * For 'once' templates, both dates are today.
 */
export const datesForTemplate = (tpl) => {
  const today = new Date();
  const pad2 = (n) => String(n).padStart(2, '0');
  const fmt = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  if (tpl.recurrence === 'once') {
    return { startDate: fmt(today), endDate: fmt(today) };
  }
  const end = new Date(today);
  end.setDate(end.getDate() + Math.max(0, (tpl.durationDays || 1) - 1));
  return { startDate: fmt(today), endDate: fmt(end) };
};
